import React, { useEffect, useRef, useState } from 'react';

/**
 * FacebookLoginButton (SCRUM-1793)
 *
 * Direct SPA flow using the Facebook JavaScript SDK. On click, calls
 * `FB.login(callback, { scope: 'public_profile,email' })` with a popup,
 * receives an access token, and invokes `onSuccess({ accessToken })`.
 *
 * The SDK is loaded lazily on first click. Configure the App ID via
 * `VITE_FACEBOOK_APP_ID`. If the env var is missing, the button is
 * disabled (returns null in production-like setups; in dev it renders
 * disabled with a tooltip).
 */
export default function FacebookLoginButton({ onSuccess, onError, disabled = false }) {
  const [sdkLoading, setSdkLoading] = useState(false);
  const sdkLoadedRef = useRef(false);
  const appId = import.meta.env.VITE_FACEBOOK_APP_ID;

  useEffect(() => {
    // Listen for FB SDK init (no-op if SDK not loaded yet)
    if (typeof window !== 'undefined' && window.FB && !sdkLoadedRef.current) {
      sdkLoadedRef.current = true;
    }
  }, []);

  function loadSdk() {
    return new Promise((resolve, reject) => {
      if (sdkLoadedRef.current && window.FB) {
        resolve(window.FB);
        return;
      }

      // Inject SDK <script> if not already present
      if (!document.getElementById('facebook-jssdk')) {
        const js = document.createElement('script');
        js.id = 'facebook-jssdk';
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        js.async = true;
        js.defer = true;
        js.crossOrigin = 'anonymous';
        js.onerror = () => reject(new Error('Failed to load Facebook SDK'));
        document.body.appendChild(js);
      }

      window.fbAsyncInit = function () {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: false,
          version: 'v19.0',
        });
        sdkLoadedRef.current = true;
        resolve(window.FB);
      };

      // Safety: if SDK loads later but fbAsyncInit was set before init,
      // poll for window.FB existence.
      const start = Date.now();
      const checkInterval = setInterval(() => {
        if (window.FB) {
          clearInterval(checkInterval);
          if (!sdkLoadedRef.current) {
            window.FB.init({
              appId,
              cookie: true,
              xfbml: false,
              version: 'v19.0',
            });
            sdkLoadedRef.current = true;
          }
          resolve(window.FB);
        } else if (Date.now() - start > 8000) {
          clearInterval(checkInterval);
          reject(new Error('Facebook SDK load timed out'));
        }
      }, 100);
    });
  }

  async function handleClick() {
    if (disabled || sdkLoading || !appId) {
      if (!appId && onError) {
        onError(new Error('Facebook OAuth is not configured'));
      }
      return;
    }

    try {
      setSdkLoading(true);
      const FB = await loadSdk();

      FB.login(
        (response) => {
          setSdkLoading(false);
          if (response.status === 'connected' && response.authResponse) {
            onSuccess({ accessToken: response.authResponse.accessToken });
          } else if (response.status === 'not_authorized') {
            onError && onError(new Error('Facebook login was not authorized'));
          } else {
            // 'unknown' or null — user cancelled or error
            onError && onError(new Error('Facebook login was cancelled or failed'));
          }
        },
        { scope: 'public_profile,email' },
      );
    } catch (err) {
      setSdkLoading(false);
      onError && onError(err);
    }
  }

  const isDisabled = disabled || sdkLoading || !appId;

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      type="button"
      className="w-full flex items-center justify-center space-x-3 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed h-[44px]"
      title={!appId ? 'VITE_FACEBOOK_APP_ID is not configured' : undefined}
    >
      <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
      <span>{sdkLoading ? 'Loading…' : 'Continue with Facebook'}</span>
    </button>
  );
}
